import * as t from 'io-ts';

import { errorHandling } from '../../../src/services/errorHandling';

import { validate } from './validate';
import { applyPathParams } from './apply-path-params';
import type { TMethod } from './method';

export type DynamicPath<QQ> = (query: QQ) => string;

type MethodCfg<Q, QQ, P, PP, R, RR> = {
  path: string | DynamicPath<QQ>;
  method: TMethod;
  pathParams: t.Decoder<Q, QQ>;
  payload: t.Decoder<P, PP>;
  response: t.Decoder<R, RR>;
};

type MethodCfgQueryOnly<Q, QQ, R, RR> = {
  path: string | DynamicPath<QQ>;
  method: TMethod;
  pathParams: t.Decoder<Q, QQ>;
  response: t.Decoder<R, RR>;
};

type MethodCfgQueryOnlyWithHeaders<Q, QQ, R, RR, H, HH> = {
  path: string | DynamicPath<QQ>;
  method: TMethod;
  pathParams: t.Decoder<Q, QQ>;
  response: t.Decoder<R, RR>;
  headers: t.Decoder<H, HH>;
};

type MethodCfgPayloadOnly<P, PP, R, RR> = {
  path: string;
  method: TMethod;
  payload: t.Decoder<P, PP>;
  response: t.Decoder<R, RR>;
};

type MethodCfgPayloadOnlyWithHeaders<P, PP, R, RR, H, HH> = {
  path: string;
  method: TMethod;
  payload: t.Decoder<P, PP>;
  response: t.Decoder<R, RR>;
  headers: t.Decoder<H, HH>;
};

type MethodCfgSimple<R, RR> = {
  path: string;
  method: TMethod;
  response: t.Decoder<R, RR>;
};

type AnyMethod<Q, QQ, P, PP, R, RR, H, HH> = MethodCfg<Q, QQ, P, PP, R, RR> &
  MethodCfgQueryOnly<Q, QQ, R, RR> &
  MethodCfgQueryOnlyWithHeaders<Q, QQ, R, RR, H, HH> &
  MethodCfgPayloadOnly<P, PP, R, RR> &
  MethodCfgPayloadOnlyWithHeaders<P, PP, R, RR, H, HH> &
  MethodCfgSimple<R, RR>;

const createMethod =
  <Q, QQ extends Record<string, any>, P, PP, R, RR, H, HH>(
    cfg: AnyMethod<Q, QQ, P, PP, R, RR, H, HH>,
  ): ((args: { payload: PP; pathParams: QQ; headers: HH }) => Promise<RR>) =>
  async (args: { payload: PP; pathParams: QQ; headers: HH }) => {
    const rawPath = String(cfg.path);
    const pathParams =
      'pathParams' in cfg
        ? validate(
            <t.Decoder<Q, QQ>>(<any>cfg).pathParams,
            <Q>(<unknown>args.pathParams),
            `${rawPath} pathParams`,
          )
        : undefined;

    const payload =
      'payload' in cfg
        ? validate((<any>cfg).payload, <P>(<unknown>args.payload), `${rawPath} payload`)
        : undefined;

    const headers =
      'headers' in cfg
        ? validate((<any>cfg).headers, <H>(<unknown>args.headers), `${rawPath} headers`)
        : undefined;
    const path = applyPathParams(cfg.path, pathParams ?? <QQ>(<unknown>{}));

    const response = await cfg.method(path, payload, headers);

    try {
      const result = validate(cfg.response, response, `${rawPath} response`);

      return result;
    } catch (error) {
      errorHandling.captureException(error);

      return response;
    }
  };

type Client<M> = {
  [K in keyof M]: M[K] extends MethodCfg<unknown, infer QQ, unknown, infer PP, unknown, infer RR>
    ? (args: { payload: PP; pathParams: QQ }) => Promise<RR>
    : M[K] extends MethodCfgPayloadOnlyWithHeaders<
        unknown,
        infer PP,
        unknown,
        infer RR,
        unknown,
        infer HH
      >
    ? (args: { payload: PP; headers: HH }) => Promise<RR>
    : M[K] extends MethodCfgQueryOnlyWithHeaders<
        unknown,
        infer QQ,
        unknown,
        infer RR,
        unknown,
        infer HH
      >
    ? (args: { pathParams: QQ; headers: HH }) => Promise<RR>
    : M[K] extends MethodCfgPayloadOnly<unknown, infer PP, unknown, infer RR>
    ? (args: { payload: PP }) => Promise<RR>
    : M[K] extends MethodCfgQueryOnly<unknown, infer QQ, unknown, infer RR>
    ? (args: { pathParams: QQ }) => Promise<RR>
    : M[K] extends MethodCfgSimple<unknown, infer RR>
    ? () => Promise<RR>
    : never;
};

type CheckClientFactoryInputStruct<M> = {
  [K in keyof M]:
    | MethodCfg<unknown, any, unknown, unknown, unknown, unknown>
    | MethodCfgQueryOnly<unknown, any, unknown, unknown>
    | MethodCfgQueryOnlyWithHeaders<unknown, any, unknown, unknown, unknown, unknown>
    | MethodCfgPayloadOnlyWithHeaders<unknown, any, unknown, unknown, unknown, unknown>
    | MethodCfgPayloadOnly<unknown, any, unknown, unknown>
    | MethodCfgSimple<unknown, any>;
};

const createClient = <A, B>(
  config: A & CheckClientFactoryInputStruct<B>,
  override?: (configRef: A & CheckClientFactoryInputStruct<B>) => void,
): Client<A> => {
  let workingConfig = config;

  if (override) {
    workingConfig = Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      Object.entries(config).map(([key, value]) => [key, { ...value }]),
    ) as typeof config;
  }

  override?.(workingConfig);

  const result = {} as Client<A>;

  Object.keys(workingConfig).forEach(key => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    result[key] = createMethod(workingConfig[key]);
  });

  return result;
};

export { createClient, createMethod };
